import * as actionTypes from './actions/actionTypes';
import { updateObject } from '../global/utility';

const initialState = {
    publishing_slides: false,
    publish_result: null,
    converting_file: false,
    convert_result: null,
    convert_error: null,
    // Editor state
    loading_documents: false,
    documents: [],
    loading_document: false,
    current_document: null,
    saving_document: false,
    save_error: null,
    creating_document: false,
    importing_document: false,
    import_error: null,
    selected_slide_index: 0,
    selected_element_index: null,
    // AI Draft state
    draft_name: null,
    draft_slides: null,
    draft_images: null,
    generating_from_draft: false,
    generate_draft_error: null,
}


const publishSlideshowStart = (state, action) => {
    return updateObject(state, { publishing_slides: true, publish_result: null });
};

const publishSlideshowSuccess = (state, action) => {
    return updateObject(state, { publishing_slides: false, publish_result: action.details });
};

const publishSlideshowFailure = (state, action) => {
    return updateObject(state, { publishing_slides: false, publish_result: null });
};

const convertFileStart = (state, action) => {
    return updateObject(state, { converting_file: true, convert_result: null, convert_error: null });
};

const convertFileSuccessReducer = (state, action) => {
    return updateObject(state, { converting_file: false, convert_result: { blob: action.blob, filename: action.filename }, convert_error: null });
};

const convertFileFailure = (state, action) => {
    return updateObject(state, { converting_file: false, convert_result: null, convert_error: action.error });
};

const convertFileReset = (state, action) => {
    return updateObject(state, { converting_file: false, convert_result: null, convert_error: null });
};


const reducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.PUBLISH_SLIDESHOW_START: return publishSlideshowStart(state, action);
        case actionTypes.PUBLISH_SLIDESHOW_SUCCESS: return publishSlideshowSuccess(state, action);
        case actionTypes.PUBLISH_SLIDESHOW_FAILURE: return publishSlideshowFailure(state, action);
        case actionTypes.CONVERT_FILE_START: return convertFileStart(state, action);
        case actionTypes.CONVERT_FILE_SUCCESS: return convertFileSuccessReducer(state, action);
        case actionTypes.CONVERT_FILE_FAILURE: return convertFileFailure(state, action);
        case actionTypes.CONVERT_FILE_RESET: return convertFileReset(state, action);

        // Editor: documents list
        case actionTypes.FETCH_DOCUMENTS_START:
            return updateObject(state, { loading_documents: true });
        case actionTypes.FETCH_DOCUMENTS_SUCCESS:
            return updateObject(state, { loading_documents: false, documents: action.documents });
        case actionTypes.FETCH_DOCUMENTS_FAILURE:
            return updateObject(state, { loading_documents: false });

        // Editor: single document
        case actionTypes.FETCH_DOCUMENT_START:
            return updateObject(state, { loading_document: true });
        case actionTypes.FETCH_DOCUMENT_SUCCESS:
            return updateObject(state, { loading_document: false, current_document: action.document, selected_slide_index: 0, selected_element_index: null });
        case actionTypes.FETCH_DOCUMENT_FAILURE:
            return updateObject(state, { loading_document: false });

        // Editor: create
        case actionTypes.CREATE_DOCUMENT_START:
            return updateObject(state, { creating_document: true });
        case actionTypes.CREATE_DOCUMENT_SUCCESS:
            return updateObject(state, { creating_document: false, documents: [action.document, ...state.documents.filter(d => d.id !== action.document.id)] });
        case actionTypes.CREATE_DOCUMENT_FAILURE:
            return updateObject(state, { creating_document: false });

        // Editor: save
        case actionTypes.UPDATE_DOCUMENT_START:
            return updateObject(state, { saving_document: true, save_error: null });
        case actionTypes.UPDATE_DOCUMENT_SUCCESS:
            return updateObject(state, {
                saving_document: false,
                current_document: state.current_document ? {
                    ...state.current_document,
                    name: action.document.name,
                    updated_at: action.document.updated_at
                } : action.document,
                documents: state.documents.map(d => d.id === action.document.id ? { ...d, name: action.document.name, updated_at: action.document.updated_at } : d)
            });
        case actionTypes.UPDATE_DOCUMENT_FAILURE:
            return updateObject(state, { saving_document: false, save_error: action.error });

        // Editor: delete
        case actionTypes.DELETE_DOCUMENT_START:
            return updateObject(state, {});
        case actionTypes.DELETE_DOCUMENT_SUCCESS:
            return updateObject(state, {
                documents: state.documents.filter(d => d.id !== action.id),
                current_document: state.current_document && state.current_document.id === action.id ? null : state.current_document
            });
        case actionTypes.DELETE_DOCUMENT_FAILURE:
            return state;

        // Editor: import
        case actionTypes.IMPORT_DOCUMENT_START:
            return updateObject(state, { importing_document: true, import_error: null });
        case actionTypes.IMPORT_DOCUMENT_SUCCESS:
            return updateObject(state, { importing_document: false, import_error: null, documents: [action.document, ...state.documents.filter(d => d.id !== action.document.id)] });
        case actionTypes.IMPORT_DOCUMENT_FAILURE:
            return updateObject(state, { importing_document: false, import_error: action.error });

        // Editor: selection
        case actionTypes.SELECT_SLIDE:
            return updateObject(state, { selected_slide_index: action.index, selected_element_index: null });
        case actionTypes.SELECT_ELEMENT:
            return updateObject(state, { selected_element_index: action.index });

        // Editor: local presentation update
        case actionTypes.UPDATE_PRESENTATION_LOCAL:
            if (!state.current_document) return state;
            return updateObject(state, {
                current_document: { ...state.current_document, presentation_json: JSON.stringify(action.presentation) }
            });

        case actionTypes.REMOTE_DOCUMENT_UPDATED:
            return state; // Handled by re-fetch in the component

        // AI Draft
        case actionTypes.AI_DRAFT_SUCCESS:
            return updateObject(state, { importing_document: false, draft_name: action.draft.name, draft_slides: action.draft.slides, draft_images: action.draft.images || {} });
        case actionTypes.UPDATE_DRAFT_SLIDES:
            return updateObject(state, { draft_slides: action.slides });
        case actionTypes.CLEAR_DRAFT:
            return updateObject(state, { draft_name: null, draft_slides: null, draft_images: null, generating_from_draft: false, generate_draft_error: null });
        case actionTypes.GENERATE_FROM_DRAFT_START:
            return updateObject(state, { generating_from_draft: true, generate_draft_error: null });
        case actionTypes.GENERATE_FROM_DRAFT_FAILURE:
            return updateObject(state, { generating_from_draft: false, generate_draft_error: action.error });

        default: return state;
    }
};

export default reducer;
